import React from 'react';
import { FileText, TrendingUp, Percent, Receipt, CreditCard, PiggyBank, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета НДФЛ
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Получаем имя типа дохода
  const getIncomeTypeName = (typeId) => {
    const incomeTypes = {
      'salary': 'Заработная плата',
      'business': 'Доход от предпринимательской деятельности',
      'dividend': 'Дивиденды',
      'foreign_dividend': 'Дивиденды от иностранных компаний',
      'interest': 'Проценты по вкладам',
      'rent': 'Доход от аренды имущества',
      'sale_property': 'Продажа имущества',
      'gift': 'Подарки',
      'foreign_income': 'Доход из-за рубежа',
      'other': 'Прочие доходы'
    };
    return incomeTypes[typeId] || typeId;
  };

  // Получаем имя системы налогообложения для ИП
  const getTaxSystemName = (systemId) => {
    const taxSystems = {
      'osn': 'Общая система налогообложения (НДФЛ)',
      'usn_income': 'УСН "Доходы" (6%)',
      'usn_income_expenses': 'УСН "Доходы минус расходы" (15%)',
      'eshn': 'Единый сельскохозяйственный налог (ЕСХН)',
      'psn': 'Патентная система налогообложения (ПСН)',
      'patent': 'Патент'
    };
    return taxSystems[systemId] || systemId;
  };

  return (
    <ToolResult title="Результаты расчета НДФЛ">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Сумма НДФЛ */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Сумма НДФЛ
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatCurrency(results.taxAmount)}
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              ({formatNumber(results.effectiveTaxRate, 1)}% от дохода)
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Налоговая ставка: {results.taxRate}%
          </p>
        </div>

        {/* Доход после уплаты налога */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4"/>
            Доход после уплаты налога
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.netIncome)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Ежемесячно: {formatCurrency(results.netIncome / 12)}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Годовой доход (до вычетов)" 
          value={formatCurrency(results.grossIncome)} 
          color="indigo" 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        {results.totalDeductions > 0 && (
          <ResultItem 
            label="Сумма налоговых вычетов" 
            value={formatCurrency(results.totalDeductions)} 
            color="green" 
            icon={<PiggyBank className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Налогооблагаемый доход" 
          value={formatCurrency(results.taxableIncome)} 
          color="orange" 
          icon={<FileText className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Ставка НДФЛ" 
          value={`${results.taxRate}%`} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Эффективная налоговая ставка" 
          value={`${formatNumber(results.effectiveTaxRate, 2)}%`} 
          color="blue" 
          icon={<Percent className="w-4 h-4"/>} 
          tooltip="Соотношение уплаченного налога к общему доходу"
        />

        <ResultItem 
          label="Тип дохода" 
          value={getIncomeTypeName(formData.incomeType)} 
        />
        
        {formData.isIp && (
          <ResultItem 
            label="Система налогообложения" 
            value={getTaxSystemName(formData.ipTaxSystem)} 
          />
        )}
        
        {formData.isSelfEmployed && (
          <ResultItem 
            label="Режим самозанятого" 
            value="Налог на профессиональный доход (НПД)" 
          />
        )}
        
        <ResultItem 
          label="Налоговое резидентство" 
          value={formData.taxResidency === 'resident' ? 'Налоговый резидент РФ' : 'Налоговый нерезидент РФ'} 
        />

        {/* Информация о налоговом вычете */}
        {results.taxSaving > 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mt-2 border border-green-200 dark:border-green-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <PiggyBank className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Налоговая экономия</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Благодаря использованию налоговых вычетов вы экономите <span className="font-medium text-green-600 dark:text-green-400">{formatCurrency(results.taxSaving)}</span>.
                  Это сумма, которую вы можете вернуть из бюджета или не доплачивать в виде НДФЛ.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Информация о прогрессивной шкале */}
        {formData.taxResidency === 'resident' && formData.incomeType === 'salary' && results.grossIncome > 5000000 && (
          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 mt-2 border border-indigo-200 dark:border-indigo-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <AlertCircle className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Прогрессивная шкала НДФЛ</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Ваш доход превышает 5 млн ₽, поэтому применяется прогрессивная шкала НДФЛ: 13% с доходов до 5 млн ₽ и 15% с суммы, превышающей этот порог.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;